import { CartSnapshot } from "@dashboard/cartVisibility/types";
import { moneyCell, readonlyTextCell } from "@dashboard/components/Datagrid/customCells/cells";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import { Locale } from "@dashboard/components/Locale";
import { Sort } from "@dashboard/types";
import { getColumnSortDirectionIcon } from "@dashboard/utils/columns/getColumnSortDirectionIcon";
import { GridCell, Item } from "@glideapps/glide-data-grid";
import moment from "moment";
import { IntlShape } from "react-intl";

import { columnsMessages } from "./messages";

export type CartVisibilityListSortField = "customer" | "items" | "total" | "lastChange";

export const cartVisibilityListStaticColumnsAdapter = (
  intl: IntlShape,
  sort: Sort<CartVisibilityListSortField>,
): AvailableColumn[] =>
  [
    {
      id: "customer",
      title: intl.formatMessage(columnsMessages.customer),
      width: 250,
    },
    {
      id: "items",
      title: intl.formatMessage(columnsMessages.items),
      width: 100,
    },
    {
      id: "total",
      title: intl.formatMessage(columnsMessages.total),
      width: 150,
    },
    {
      id: "lastChange",
      title: intl.formatMessage(columnsMessages.lastChange),
      width: 250,
    },
    {
      id: "status",
      title: intl.formatMessage(columnsMessages.status),
      width: 200,
    },
  ].map(column => ({
    ...column,
    icon: getColumnSortDirectionIcon(sort, column.id),
  }));

export const createGetCellContent =
  ({
    carts,
    columns,
    locale,
    anonymousLabel,
    notPromotedLabel,
  }: {
    carts: CartSnapshot[];
    columns: AvailableColumn[];
    locale: Locale;
    anonymousLabel: string;
    notPromotedLabel: string;
  }) =>
  ([column, row]: Item): GridCell => {
    const rowData = carts[row];
    const columnId = columns[column]?.id;

    if (!columnId || !rowData) {
      return readonlyTextCell("");
    }

    switch (columnId) {
      case "customer":
        return readonlyTextCell(rowData.email || anonymousLabel);
      case "items":
        return readonlyTextCell(String(rowData.lineCount ?? 0));
      case "total":
        return moneyCell(rowData.totalGross ?? 0, rowData.currency ?? "", {
          cursor: "pointer",
          readonly: true,
        });
      case "lastChange":
        return readonlyTextCell(moment(rowData.lastChange).locale(locale).format("lll"));
      case "status":
        return readonlyTextCell(
          rowData.draftOrder ? `Draft #${rowData.draftOrder.number}` : notPromotedLabel,
        );
      default:
        return readonlyTextCell("");
    }
  };
